import { useBoolean, useToast } from '@chakra-ui/react'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode
} from 'react'

import { getGroupAprovado, getGroupFornecedores } from '@helper/mrp/approval'
import mrp from '@service/mrp'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  IApprovalContext,
  IApprovalFilterDates,
  IApprovalFilterParams,
  IPendingOrderItem,
  IPendingOrderGroup,
  IPendingOrdersFilterRequest,
  IApprovalRequest,
  ApprovalTabType
} from '@base/@types/providers/Mrp/ApprovalProvider'

const initialFilterParams: IApprovalFilterParams = {
  lojasIds: [],
  categoriasCompactasIds: [],
  gerentesProdutoIds: [],
  usuariosSolicitantesIds: []
}

const initialFilterDates: IApprovalFilterDates = {
  initialDate: '',
  finalDate: ''
}

// Monta o body do POST Pendentes/Filtrar; campos vazios não são enviados (= sem filtro)
const buildFilterRequest = (
  params: IApprovalFilterParams,
  dates: IApprovalFilterDates
): IPendingOrdersFilterRequest => {
  const request: IPendingOrdersFilterRequest = {}

  Object.keys(params).forEach(key => {
    const ids = (params[key as keyof IApprovalFilterParams] || []).map(item =>
      Number(item.value)
    )
    if (ids.length > 0) request[key as keyof IApprovalFilterParams] = ids
  })

  // Datas já vêm do input como yyyy-MM-dd (sem hora/fuso)
  if (dates.initialDate) request.dataSolicitacaoInicio = dates.initialDate
  if (dates.finalDate) request.dataSolicitacaoFim = dates.finalDate

  return request
}

interface IApprovalHistoryItem extends IPendingOrderItem {
  // Filtro aplicado quando o pedido foi aprovado/rejeitado
  filtroNaAcao?: IPendingOrdersFilterRequest
}

// Histórico local da sessão: aplica o filtro vigente nos campos que o item possui.
// Gerente de produto não vem no item; nesse caso o item só aparece se foi
// aprovado/rejeitado sob um filtro de gerente contido no filtro atual.
const matchesFilter = (
  order: IApprovalHistoryItem,
  request: IPendingOrdersFilterRequest
): boolean => {
  const includes = (ids: number[] | undefined, value: number) =>
    !ids || ids.length === 0 || ids.includes(Number(value))

  if (!includes(request.lojasIds, order.codigoLoja)) return false
  if (
    !includes(request.categoriasCompactasIds, order.codigoCategoriaCompacta)
  ) {
    return false
  }
  if (!includes(request.usuariosSolicitantesIds, order.usuarioSolicitanteId)) {
    return false
  }

  const dia = (order.dataSolicitacao || '').slice(0, 10)
  if (request.dataSolicitacaoInicio && dia < request.dataSolicitacaoInicio) {
    return false
  }
  if (request.dataSolicitacaoFim && dia > request.dataSolicitacaoFim) {
    return false
  }

  const gerentes = request.gerentesProdutoIds
  if (gerentes && gerentes.length > 0) {
    const gerentesNaAcao = order.filtroNaAcao?.gerentesProdutoIds
    if (!gerentesNaAcao || gerentesNaAcao.length === 0) return false
    if (!gerentesNaAcao.every(id => gerentes.includes(id))) return false
  }

  return true
}

interface ApprovalProviderProps {
  children: ReactNode
}

const ApprovalProvider: FC<ApprovalProviderProps> = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [allOrders, setAllOrders] = useState<IPendingOrderItem[]>([])
  const [approvedOrdersHistory, setApprovedOrdersHistory] = useState<
    IApprovalHistoryItem[]
  >([])
  const [rejectedOrdersHistory, setRejectedOrdersHistory] = useState<
    IApprovalHistoryItem[]
  >([])
  const [currentPage, setCurrentPage] = useState(1)
  const [activeTab, setActiveTab] = useState<ApprovalTabType>('pendentes')
  const itemsPerPage = 10

  // Filtros
  const [filters, setFilters] = useBoolean(true)
  const [params, setParams] =
    useState<IApprovalFilterParams>(initialFilterParams)
  const [paramsDates, setParamsDates] =
    useState<IApprovalFilterDates>(initialFilterDates)
  // Filtros aplicados na última busca (refresh após aprovar mantém o filtro).
  // O ref é lido pelo fetch; o state re-renderiza o histórico filtrado.
  const appliedRequest = useRef<IPendingOrdersFilterRequest>({})
  const [appliedFilter, setAppliedFilter] =
    useState<IPendingOrdersFilterRequest>({})
  // Sequência das buscas: respostas de buscas antigas são descartadas
  const fetchSequence = useRef(0)

  const setAppliedRequest = useCallback(
    (request: IPendingOrdersFilterRequest) => {
      appliedRequest.current = request
      setAppliedFilter(request)
    },
    []
  )

  const pendingOrders = useMemo(() => {
    // Filtrar apenas pedidos que não foram aprovados nem rejeitados
    return allOrders.filter(
      order => order.aprovado === null || order.aprovado === undefined
    )
  }, [allOrders])

  // Pedidos que o endpoint já trouxe: o histórico local não pode repeti-los
  // (senão, após recarregar, itens e totais aparecem em dobro)
  const pedidosDoEndpoint = useMemo(
    () => new Set(allOrders.map(order => order.idRequestSap)),
    [allOrders]
  )

  const approvedOrders = useMemo(() => {
    // Pedidos aprovados do endpoint (se houver) + histórico desta sessão,
    // respeitando o filtro aplicado
    const fromEndpoint = allOrders.filter(order => order.aprovado === true)
    return [
      ...fromEndpoint,
      ...approvedOrdersHistory.filter(
        order =>
          !pedidosDoEndpoint.has(order.idRequestSap) &&
          matchesFilter(order, appliedFilter)
      )
    ]
  }, [allOrders, approvedOrdersHistory, pedidosDoEndpoint, appliedFilter])

  const rejectedOrders = useMemo(() => {
    // Pedidos rejeitados do endpoint (se houver) + histórico desta sessão,
    // respeitando o filtro aplicado
    const fromEndpoint = allOrders.filter(order => order.aprovado === false)
    return [
      ...fromEndpoint,
      ...rejectedOrdersHistory.filter(
        order =>
          !pedidosDoEndpoint.has(order.idRequestSap) &&
          matchesFilter(order, appliedFilter)
      )
    ]
  }, [allOrders, rejectedOrdersHistory, pedidosDoEndpoint, appliedFilter])

  const currentOrders = useMemo(() => {
    switch (activeTab) {
      case 'aprovados':
        return approvedOrders
      case 'rejeitados':
        return rejectedOrders
      default:
        return pendingOrders
    }
  }, [activeTab, pendingOrders, approvedOrders, rejectedOrders])

  const groupedOrders = useMemo<IPendingOrderGroup[]>(() => {
    if (!Array.isArray(currentOrders)) {
      return []
    }

    const groupsById = new Map<string, IPendingOrderGroup>()

    currentOrders.forEach(item => {
      const existing = groupsById.get(item.idRequestSap)

      if (existing) {
        existing.items.push(item)
        existing.totalRecomendado += item.quantidadeRecomendada
        existing.totalDesejado += item.quantidadeDesejada
        existing.hasAlteracoes =
          existing.hasAlteracoes || item.quantidadeDesejadaAlterada
      } else {
        groupsById.set(item.idRequestSap, {
          idRequestSap: item.idRequestSap,
          // Derivado de todos os itens logo abaixo
          fornecedores: [],
          items: [item],
          totalItems: 1,
          totalRecomendado: item.quantidadeRecomendada,
          totalDesejado: item.quantidadeDesejada,
          hasAlteracoes: item.quantidadeDesejadaAlterada,
          percentualAlteracaoGeral: 0,
          dataSolicitacao: item.dataSolicitacao,
          // Derivado de todos os itens logo abaixo
          aprovado: null,
          usuarioSolicitanteLogin: item.usuarioSolicitanteLogin,
          tipoProcesso: item.tipoProcesso,
          foraCronograma: !!item.foraCronograma,
          justificativaForaCronograma: item.justificativaForaCronograma ?? null
        })
      }
    })

    const groups = Array.from(groupsById.values())

    // Percentual de alteração geral, status e fornecedores derivados de todos os itens
    groups.forEach(group => {
      if (group.totalRecomendado > 0) {
        group.percentualAlteracaoGeral =
          ((group.totalDesejado - group.totalRecomendado) /
            group.totalRecomendado) *
          100
      }
      group.totalItems = group.items.length
      group.aprovado = getGroupAprovado(group.items)
      group.fornecedores = getGroupFornecedores(group.items)
    })

    return groups.sort(
      (a, b) =>
        new Date(b.dataSolicitacao).getTime() -
        new Date(a.dataSolicitacao).getTime()
    )
  }, [currentOrders])

  const totalPages = Math.ceil(groupedOrders.length / itemsPerPage)

  const paginatedGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return groupedOrders.slice(startIndex, endIndex)
  }, [groupedOrders, currentPage])

  // Ao remover pedidos (aprovação/rejeição/filtro) a página atual pode deixar de existir
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const fetchOrders = useCallback(async () => {
    const sequence = ++fetchSequence.current
    const isObsolete = () => sequence !== fetchSequence.current
    setLoading(true)

    try {
      const { data } = await mrp.postFilterPendingOrders(appliedRequest.current)
      if (isObsolete()) return
      // 200 sem conteúdo (null/objeto vazio) = lista vazia
      setAllOrders(Array.isArray(data?.data) ? data.data : [])
    } catch (err: any) {
      if (isObsolete()) return

      const status = err?.response?.status
      const errorMessage =
        err?.response?.data?.message || 'Erro ao carregar pedidos'

      setAllOrders([]) // Garantir que seja sempre um array

      // 404 ou 400 "Nenhum dado encontrado" = lista vazia, não é erro
      const isEmptyResult =
        status === 404 ||
        (status === 400 && /nenhum dado encontrado/i.test(String(errorMessage)))
      if (isEmptyResult) return

      toast({
        title: 'Erro',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      if (!isObsolete()) setLoading(false)
    }
  }, [toast])

  const approveOrder = useCallback(
    async (request: IApprovalRequest) => {
      let success = false
      setLoading(true)

      try {
        await mrp.postApproveOrder(request)

        // Move todos os itens do pedido para o histórico da sessão, preservando
        // justificativa/observação/data da ação para a tela de visualização
        const dataAcaoAprovador = new Date().toISOString()
        const filtroNaAcao = appliedRequest.current
        const movedItems: IApprovalHistoryItem[] = allOrders
          .filter(order => order.idRequestSap === request.idRequestSap)
          .map(item => ({
            ...item,
            aprovado: request.aprovar,
            justificativaAprovador: request.justificativa,
            observacaoAprovador: request.observacao || '',
            dataAcaoAprovador,
            filtroNaAcao
          }))

        if (movedItems.length > 0) {
          const setHistory = request.aprovar
            ? setApprovedOrdersHistory
            : setRejectedOrdersHistory
          setHistory(prev => [
            ...prev.filter(
              order => order.idRequestSap !== request.idRequestSap
            ),
            ...movedItems
          ])
        }

        // Remover da lista principal (a página é ajustada pelo effect de totalPages)
        setAllOrders(prev =>
          prev.filter(order => order.idRequestSap !== request.idRequestSap)
        )

        toast({
          title: 'Sucesso',
          description: `Pedido ${
            request.aprovar ? 'aprovado' : 'rejeitado'
          } com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        success = true
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message || 'Erro ao processar aprovação'
        toast({
          title: 'Erro',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      } finally {
        setLoading(false)
      }
      // Quem chamou só fecha o modal no sucesso (no erro mantém ajustes/justificativa)
      return success
    },
    [toast, allOrders]
  )

  const handleTabChange = useCallback((tab: ApprovalTabType) => {
    setActiveTab(tab)
    setCurrentPage(1) // Reset página ao trocar aba
  }, [])

  const handleUpdateParams = useCallback(
    ({ name, value }: { name: string; value: any }) => {
      if (name === 'initialDate' || name === 'finalDate') {
        setParamsDates(prev => ({ ...prev, [name]: value || '' }))
        return
      }

      setParams(prev => ({
        ...prev,
        [name]: Array.isArray(value) ? value : value ? [value] : []
      }))
    },
    []
  )

  const applyFilters = useCallback(async () => {
    const { initialDate, finalDate } = paramsDates
    if (initialDate && finalDate && initialDate > finalDate) {
      toast({
        title: 'Atenção',
        description:
          'A data de solicitação inicial não pode ser maior que a data final',
        status: 'warning',
        duration: 4000,
        isClosable: true
      })
      return
    }

    setAppliedRequest(buildFilterRequest(params, paramsDates))
    setCurrentPage(1)
    await fetchOrders()
  }, [params, paramsDates, fetchOrders, toast, setAppliedRequest])

  const clearAllFilters = useCallback(async () => {
    setParams(initialFilterParams)
    setParamsDates(initialFilterDates)
    setAppliedRequest({})
    setCurrentPage(1)
    await fetchOrders()
  }, [fetchOrders, setAppliedRequest])

  // Filtro aplicado na última busca
  const hasActiveFilters = Object.keys(appliedFilter).length > 0

  const filtersList = useMemo<ILayoutFilter[]>(
    () => [
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true,
        pagination: true
      },
      {
        id: 'dataSolicitacao',
        label: 'Data da Solicitação',
        type: 'date-input'
      },
      {
        id: 'categoriasCompactasIds',
        label: 'Categoria Compacta',
        filterId: 'compactCategories',
        type: 'select',
        isMulti: true,
        pagination: true
      },
      {
        id: 'gerentesProdutoIds',
        label: 'Gerente de Produto',
        filterId: 'productManagers',
        type: 'select',
        isMulti: true,
        pagination: true
      },
      {
        id: 'usuariosSolicitantesIds',
        label: 'Usuário',
        filterId: 'solicitantes',
        type: 'select',
        isMulti: true,
        pagination: true
      }
    ],
    []
  )

  const contextValue = useMemo<IApprovalContext>(
    () => ({
      loading,
      allOrders,
      groupedOrders,
      currentPage,
      itemsPerPage,
      totalPages,
      activeTab,
      paginatedGroups,
      pendingOrders,
      approvedOrders,
      rejectedOrders,
      fetchOrders,
      approveOrder,
      setCurrentPage,
      setActiveTab: handleTabChange,
      filters,
      setFilters,
      filtersList,
      params,
      paramsDates,
      hasActiveFilters,
      handleUpdateParams,
      applyFilters,
      clearAllFilters
    }),
    [
      loading,
      allOrders,
      groupedOrders,
      currentPage,
      totalPages,
      activeTab,
      paginatedGroups,
      pendingOrders,
      approvedOrders,
      rejectedOrders,
      fetchOrders,
      approveOrder,
      handleTabChange,
      filters,
      setFilters,
      filtersList,
      params,
      paramsDates,
      hasActiveFilters,
      handleUpdateParams,
      applyFilters,
      clearAllFilters
    ]
  )

  return (
    <ApprovalContext.Provider value={contextValue}>
      {children}
    </ApprovalContext.Provider>
  )
}

export default ApprovalProvider
export const ApprovalContext = createContext<IApprovalContext | undefined>(
  undefined
)
